"use client";

import { c as _c } from "react/compiler-runtime";
import { formatCents } from "@/lib/utils/formatMoney";
import { IconCryptoWallet } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconArrowLeftOutline18, IconBrainOutline18, IconCheckOutline18, IconCodeBranchOutline18, IconCreditCardOutline18, IconLayersOutline18, IconMonitorOutline18, IconServerOutline18, IconUserGroupOutline18 } from "nucleo-ui-outline-18";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { usePlanOptions } from "@/hooks/usePlanOptions";
import { invalidateAllBillingQueries, useCreateCheckoutSession, useDowngradeSubscription, usePreviewUpgrade, useReactivateSubscription, useUpgradeSubscription } from "@/hooks/useStripeBilling";
import { useSubscription } from "@/hooks/useSubscription";
import { useListTiers } from "@/hooks/useTiers";
import { openSupport } from "@/lib/support";
import toast from "@/lib/toast";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/utils/formatNumber";
import type { BillingInterval } from "@/types/billing";
import { getTierDisplayName } from "@/types/billing";
import { YEARLY_DISCOUNT_PERCENT } from "@/app/(public)/pricing/_components/pricing-data";
import { useModalStore } from "@b3dotfun/sdk/global-account/react";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { stripePromise } from "@/lib/stripe";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import type { BrailleSpinnerName } from "unicode-animations";
import spinners from "unicode-animations";

// AnySpend billing constants
const USDC_BASE_ADDRESS = "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913";
const USDC_CHAIN_ID = 8453;
const BILLING_RECIPIENT = process.env.NEXT_PUBLIC_B3_BILLING_RECIPIENT ?? "";
const BILLING_WORKFLOW_ID = process.env.NEXT_PUBLIC_B3_BILLING_WORKFLOW_ID ?? "";
const BILLING_ORG_ID = process.env.NEXT_PUBLIC_B3_BILLING_ORG_ID ?? "";

// Tier ordering for upgrade/downgrade determination
const TIER_ORDER: Record<string, number> = {
  free: 0,
  starter: 1,
  pro: 2,
  biz: 3
};
type Step = "select-tier" | "payment" | "downgrade-confirm" | "success";
type PaymentMethod = "stripe" | "anyspend";
interface UpgradePlanModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentTier: string;
  currentInterval: BillingInterval;
  orgId: string;
  cancelAt?: string;
  pendingTier?: string;
  pendingTierAt?: string;
  pendingInterval?: BillingInterval;
  initialSelectedTier?: string;
}
export function UpgradePlanModal({
  open,
  onOpenChange,
  currentTier,
  currentInterval,
  orgId,
  cancelAt,
  pendingTier,
  pendingTierAt,
  pendingInterval,
  initialSelectedTier
}: UpgradePlanModalProps) {
  const isCurrentlyYearly = currentInterval === "year";
  const [step, setStep] = useState<Step>("select-tier");
  const [selectedTier, setSelectedTier] = useState<string | null>(initialSelectedTier ?? (currentTier === "free" ? null : currentTier));
  const [isYearly, setIsYearly] = useState(isCurrentlyYearly);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("stripe");
  const [error, setError] = useState<string | null>(null);
  const [effectiveAt, setEffectiveAt] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isWaitingForUpdate, setIsWaitingForUpdate] = useState(false);
  const queryClient = useQueryClient();
  const {
    data: tiers,
    isLoading: tiersLoading
  } = useListTiers();
  const subscriptionQuery = useSubscription(orgId, {
    refetchInterval: isWaitingForUpdate ? 1000 : undefined
  });
  const {
    data: planOptions
  } = usePlanOptions(orgId);
  const {
    data: upgradePreview
  } = usePreviewUpgrade(orgId, {
    enabled: open && currentTier !== "free"
  });
  const createCheckoutSession = useCreateCheckoutSession(orgId);
  const upgradeSubscription = useUpgradeSubscription(orgId);
  const downgradeSubscription = useDowngradeSubscription(orgId);
  const reactivateSubscription = useReactivateSubscription(orgId);
  const handleReactivate = useCallback(async () => {
    setError(null);
    try {
      await reactivateSubscription.mutateAsync();
      const tierName = getTierDisplayName(currentTier);
      const label = cancelAt ? `Staying on ${tierName}` : pendingTier ? `Staying on ${tierName} — downgrade cancelled` : `Staying on ${tierName}`;
      toast.success(label);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to cancel pending change");
    }
  }, [reactivateSubscription, currentTier, cancelAt, pendingTier]);
  const setB3ModalOpen = useModalStore(state => state.setB3ModalOpen);
  const setB3ModalContentType = useModalStore(state_0 => state_0.setB3ModalContentType);

  // Sync state when modal transitions from closed → open (prevents stale
  // isYearly/selectedTier when the component stays mounted across opens).
  const wasOpenRef = useRef(open);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setIsYearly(isCurrentlyYearly);
      setSelectedTier(initialSelectedTier ?? (currentTier === "free" ? null : currentTier));
    }
    wasOpenRef.current = open;
  }, [open, isCurrentlyYearly, currentTier, initialSelectedTier]);
  const invalidateBillingQueries = useCallback(() => {
    invalidateAllBillingQueries(queryClient);
  }, [queryClient]);
  const selectedTierInfo = tiers?.find(t => t.name.toLowerCase() === selectedTier);
  const isIntervalDowngrade = selectedTier === currentTier && !isYearly && isCurrentlyYearly;
  const isDowngrade = isIntervalDowngrade || (selectedTier ? (TIER_ORDER[selectedTier] ?? 0) < (TIER_ORDER[currentTier] ?? 0) : false);
  const resetState = useCallback(() => {
    setStep("select-tier");
    setSelectedTier(currentTier === "free" ? null : currentTier);
    setIsYearly(isCurrentlyYearly);
    setError(null);
    setEffectiveAt(null);
    setPaymentMethod("stripe");
    setClientSecret(null);
    setIsWaitingForUpdate(false);
  }, [currentTier, isCurrentlyYearly]);
  const handleOpenChange = useCallback((open_0: boolean) => {
    if (!open_0) resetState();
    onOpenChange(open_0);
  }, [onOpenChange, resetState]);
  const selectedInterval = isYearly ? "year" : "month";
  // If there's a pending interval change, selecting the current tier+interval
  // should NOT be treated as "same plan" — user wants to undo the pending change.
  const hasPendingIntervalChange = !!pendingInterval && pendingInterval !== currentInterval;
  const isSamePlan = selectedTier === currentTier && selectedInterval === currentInterval && !hasPendingIntervalChange;
  // When a downgrade is already pending, still allow upgrades that land
  // strictly above the current tier (they supersede the pending change).
  // Same-tier swaps and further downgrades stay blocked.
  const isUpgradeOverCurrent = !!selectedTier && (TIER_ORDER[selectedTier] ?? 0) > (TIER_ORDER[currentTier] ?? 0);
  const isCrossIntervalUpgrade = selectedTier === currentTier && isYearly && !isCurrentlyYearly;
  // Allow undoing a pending interval-only change by re-selecting the current tier+interval.
  const isUndoPendingIntervalChange = pendingTier === currentTier && selectedTier === currentTier && selectedInterval === currentInterval && hasPendingIntervalChange;
  const pendingTierBlocks = !!pendingTier && !isUpgradeOverCurrent && !isCrossIntervalUpgrade && !isUndoPendingIntervalChange;
  const handleContinue = useCallback(async () => {
    if (!selectedTier || !selectedTierInfo) return;
    setError(null);
    setEffectiveAt(null);

    // Same plan (tier + interval) — no action
    if (isSamePlan) return;

    // Undo pending interval-only change via reactivate (clears PendingTier/PendingInterval)
    if (isUndoPendingIntervalChange) {
      try {
        await reactivateSubscription.mutateAsync();
        invalidateBillingQueries();
        setStep("success");
      } catch (err_0) {
        setError(err_0 instanceof Error ? err_0.message : "Failed to undo pending change");
      }
      return;
    }

    // Downgrade flow
    if (isDowngrade) {
      setStep("downgrade-confirm");
      return;
    }

    // Upgrade flow — go to payment step
    setStep("payment");
  }, [selectedTier, selectedTierInfo, isSamePlan, isUndoPendingIntervalChange, reactivateSubscription, invalidateBillingQueries, isDowngrade]);
  const handleStripeNewSubscription = useCallback(async () => {
    if (!selectedTier) return;
    setError(null);
    try {
      const result = await createCheckoutSession.mutateAsync({
        tier: selectedTier,
        interval: selectedInterval
      });
      setClientSecret(result.clientSecret);
    } catch (err_1) {
      setError(err_1 instanceof Error ? err_1.message : "Failed to create checkout session");
    }
  }, [selectedTier, selectedInterval, createCheckoutSession]);
  const tierWhenWaitStarted = useRef(currentTier);
  const intervalWhenWaitStarted = useRef(currentInterval);
  const waitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // When internal polling detects tier/interval change via updated props, transition to success.
  // If no change detected within 20s, stop waiting and show warning.
  useEffect(() => {
    if (!isWaitingForUpdate) {
      if (waitTimeoutRef.current) {
        clearTimeout(waitTimeoutRef.current);
        waitTimeoutRef.current = null;
      }
      return;
    }
    const tierChanged = currentTier !== tierWhenWaitStarted.current;
    const intervalChanged = currentInterval !== intervalWhenWaitStarted.current;
    if (tierChanged || intervalChanged) {
      setIsWaitingForUpdate(false);
      invalidateBillingQueries();
      setStep("success");
      return;
    }
    // Start timeout to escape spinner if webhook is slow.
    if (!waitTimeoutRef.current) {
      waitTimeoutRef.current = setTimeout(() => {
        setIsWaitingForUpdate(false);
        setStep("success");
        setError("Your plan update is still processing. It may take a moment to reflect.");
        waitTimeoutRef.current = null;
      }, 20_000);
    }
    return () => {
      if (waitTimeoutRef.current) {
        clearTimeout(waitTimeoutRef.current);
        waitTimeoutRef.current = null;
      }
    };
  }, [isWaitingForUpdate, currentTier, currentInterval, invalidateBillingQueries]);
  const handleEmbeddedCheckoutComplete = useCallback(() => {
    setClientSecret(null);
    tierWhenWaitStarted.current = currentTier;
    intervalWhenWaitStarted.current = currentInterval;
    setIsWaitingForUpdate(true);
  }, [currentTier, currentInterval]);
  const handleStripeUpgrade = useCallback(async () => {
    if (!selectedTier) return;
    setError(null);
    try {
      await upgradeSubscription.mutateAsync({
        newTier: selectedTier,
        interval: selectedInterval
      });
      invalidateBillingQueries();
      setStep("success");
    } catch (err_2) {
      setError(err_2 instanceof Error ? err_2.message : "Failed to upgrade subscription");
    }
  }, [selectedTier, selectedInterval, upgradeSubscription, invalidateBillingQueries]);
  const handleDowngradeConfirm = useCallback(async () => {
    if (!selectedTier) return;
    setError(null);
    try {
      const result_0 = await downgradeSubscription.mutateAsync({
        newTier: selectedTier,
        interval: selectedInterval
      });
      setEffectiveAt(result_0.effectiveAt);
      setStep("success");
    } catch (err_3) {
      setError(err_3 instanceof Error ? err_3.message : "Failed to downgrade subscription");
    }
  }, [selectedTier, selectedInterval, downgradeSubscription]);
  const handleAnySpendPayment = useCallback(() => {
    if (!selectedTier || !selectedTierInfo) return;

    // Guard against missing env vars — silent payment-flow failures would send
    // USDC to address(0) / empty workflow and the user would have no feedback.
    if (!BILLING_RECIPIENT || !BILLING_WORKFLOW_ID || !BILLING_ORG_ID) {
      setError("Crypto checkout is unavailable right now. Please try card payment or contact support.");
      return;
    }

    // Convert cents to USDC wei (6 decimals): priceUSDCents * 10_000
    const priceCents = isYearly ? selectedTierInfo.annualPriceUSDCents : selectedTierInfo.priceUSDCents;
    const totalAmount = (BigInt(priceCents) * BigInt(10_000)).toString();
    setB3ModalContentType({
      type: "anySpendCheckoutTrigger",
      recipientAddress: BILLING_RECIPIENT,
      destinationTokenAddress: USDC_BASE_ADDRESS,
      destinationTokenChainId: USDC_CHAIN_ID,
      totalAmount,
      organizationName: "B3OS",
      workflowId: BILLING_WORKFLOW_ID,
      orgId: BILLING_ORG_ID,
      buttonText: `Subscribe to ${getTierDisplayName(selectedTierInfo.name)}`,
      callbackMetadata: {
        inputs: {
          tier: selectedTier,
          customerOrgId: orgId,
          interval: selectedInterval
        }
      },
      onSuccess: () => {
        invalidateBillingQueries();
        setStep("success");
      }
    });
    setB3ModalOpen(true);
  }, [selectedTier, selectedTierInfo, isYearly, orgId, setB3ModalContentType, setB3ModalOpen, invalidateBillingQueries]);
  const priceLabel = selectedTierInfo ? isYearly ? `$${formatCents(Math.round(selectedTierInfo.annualPriceUSDCents / 12))}/mo` : `$${formatCents(selectedTierInfo.priceUSDCents)}/mo` : "";
  const chargeLabel = selectedTierInfo ? isYearly ? `$${formatCents(selectedTierInfo.annualPriceUSDCents)}/yr` : `$${formatCents(selectedTierInfo.priceUSDCents)}/mo` : "";
  return <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={cn(step === "select-tier" ? "max-w-[1120px]" : clientSecret ? "max-w-3xl" : "max-w-2xl")}>
        {/* Step 1: Tier Selection */}
        {step === "select-tier" && <>
            <DialogHeader className="border-ds-border-default border-b pb-4">
              <div className="flex items-start gap-4">
                <div className="border-ds-border-default bg-ds-surface-card flex size-10 shrink-0 items-center justify-center rounded-lg border shadow-[0_1px_2px_rgba(0,0,0,0.05),inset_0_0_0_1px_rgba(0,0,0,0.18),inset_0_-2px_0_rgba(0,0,0,0.05)]">
                  <IconLayersOutline18 className="text-ds-text-secondary h-5 w-5" />
                </div>
                <div>
                  <DialogTitle className="text-base font-semibold">Select plan</DialogTitle>
                  <DialogDescription className="text-ds-text-muted text-sm">
                    Simple and flexible per-user pricing.
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {cancelAt && <PendingChangeBanner onKeep={handleReactivate} isLoading={reactivateSubscription.isPending} keepLabel={`Keep ${getTierDisplayName(currentTier)}`}>
                Pending cancellation on <strong>{new Date(cancelAt).toLocaleDateString()}</strong>. Reactivate to make
                changes.
              </PendingChangeBanner>}

            {pendingTier && !cancelAt && <PendingChangeBanner onKeep={handleReactivate} isLoading={reactivateSubscription.isPending} keepLabel={`Keep ${getTierDisplayName(currentTier)}`}>
                {pendingTier === currentTier && pendingInterval ? <>
                    Pending switch to{" "}
                    <strong>
                      {getTierDisplayName(pendingTier)} {pendingInterval === "year" ? "Yearly" : "Monthly"}
                    </strong>
                  </> : <>
                    Pending downgrade to <strong>{getTierDisplayName(pendingTier)}</strong>
                  </>}
                {pendingTierAt && <>
                    {" "}
                    on <strong>{new Date(pendingTierAt).toLocaleDateString()}</strong>
                  </>}
                . You can still upgrade to a higher tier — that supersedes the pending change.
              </PendingChangeBanner>}

            {/* Billing interval toggle */}
            <div className="flex items-center justify-center pt-4">
              <div role="group" aria-label="Billing interval" className="bg-ds-neutral-100 dark:bg-ds-neutral-800 border-ds-border-default relative inline-flex rounded-full border p-1">
                <button type="button" aria-pressed={!isYearly} onClick={() => setIsYearly(false)} className={cn("relative rounded-full px-4 py-1.5 text-sm font-medium transition-colors", !isYearly ? "bg-ds-surface-card text-ds-text-primary shadow-sm" : "text-ds-text-muted hover:text-ds-text-subtle")}>
                  Monthly
                </button>
                <button type="button" aria-pressed={isYearly} onClick={() => setIsYearly(true)} className={cn("relative inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition-colors", isYearly ? "bg-ds-surface-card text-ds-text-primary shadow-sm" : "text-ds-text-muted hover:text-ds-text-subtle")}>
                  Yearly
                  <span className="text-ds-success-accent inline-flex items-center gap-1 text-xs">
                    <span className="bg-ds-success-accent size-1.5 rounded-full" />
                    {YEARLY_DISCOUNT_PERCENT}% OFF
                  </span>
                </button>
              </div>
            </div>

            {tiersLoading ? <div className="flex items-center justify-center py-16">
                <UnicodeSpinner name="orbit" className="text-ds-text-disabled text-xl" />
              </div> : <div className="flex flex-col gap-6 pt-4">
                <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
                  {tiers?.map(tier => {
              const tierId = tier.name.toLowerCase();
              const isSelected = tierId === selectedTier;
              const displayPrice = isYearly ? tier.annualPriceUSDCents / 100 / 12 : tier.priceUSDCents / 100;
              // Disable free tier when user is already on free (only allow for paid → free downgrade)
              const isDisabled = tierId === "free" && currentTier === "free";
              return <button key={tierId} type="button" disabled={isDisabled} onClick={() => setSelectedTier(tierId)} className={cn("rounded-ds-2xl bg-ds-neutral-100 dark:bg-ds-neutral-800 relative flex flex-col text-left transition-all duration-200", isDisabled && "cursor-not-allowed opacity-50", isSelected ? "border-ds-brand-500 border-[1.5px]" : "border-ds-border-default hover:border-ds-neutral-300 border dark:hover:border-zinc-600")}>
                        {/* Header */}
                        <div className="flex items-center gap-2 px-5 py-3">
                          {tierId !== "free" && <TierDots tier={tierId} />}
                          <span className="text-ds-text-subtle font-mono text-base font-medium uppercase tracking-[-0.32px]">
                            {getTierDisplayName(tierId)}
                          </span>
                        </div>
                        {/* Body — inset with padding from outer card */}
                        <div className={cn("rounded-ds-xl bg-ds-surface-card mx-1 mb-1 flex flex-1 flex-col p-3", isSelected ? "border-ds-brand-500 border-[1.5px]" : "border-ds-border-default border")}>
                          <div className="flex flex-col gap-2 p-3">
                            {(() => {
                      const previewKey = `${tierId}:${selectedInterval}`;
                      const preview = upgradePreview?.previews[previewKey];
                      const isUpgradeTarget = currentTier !== "free" && preview != null && preview.amountDueCents > 0 && !(tierId === currentTier && selectedInterval === currentInterval);
                      return <div className="flex flex-col gap-1">
                                  <div className="flex items-center gap-2">
                                    {isUpgradeTarget ? <>
                                        <span className="text-ds-text-muted leading-label text-lg font-semibold line-through">
                                          {(() => {
                                const fullPrice = isYearly ? tier.annualPriceUSDCents / 100 : tier.priceUSDCents / 100;
                                return fullPrice.toFixed(fullPrice % 1 === 0 ? 0 : 2);
                              })()}
                                        </span>
                                        <span className="text-ds-text-primary leading-label text-3xl font-semibold">
                                          {(preview.amountDueCents / 100).toFixed(preview.amountDueCents / 100 % 1 === 0 ? 0 : 2)}
                                        </span>
                                        <span className="text-ds-text-muted text-xs font-medium leading-4 tracking-tight">
                                          USD
                                          <br />
                                          due now
                                        </span>
                                      </> : <>
                                        <span className="text-ds-text-primary leading-label text-3xl font-semibold">
                                          {tier.priceUSDCents === 0 ? "0" : displayPrice.toFixed(displayPrice % 1 === 0 ? 0 : 2)}
                                        </span>
                                        <span className="text-ds-text-muted text-xs font-medium leading-4 tracking-tight">
                                          USD /
                                          <br />
                                          month
                                        </span>
                                      </>}
                                  </div>
                                  {isUpgradeTarget && <p className="text-ds-text-muted text-2xs leading-label">
                                      pro-rated for this cycle
                                    </p>}
                                </div>;
                    })()}
                            {isYearly && tier.priceUSDCents > 0 && <p className="text-ds-text-muted text-2xs leading-label">billed annually</p>}

                            <div className="flex flex-col gap-1 py-2.5">
                              <PlanFeature icon={<IconCodeBranchOutline18 className="h-5 w-5" />} value={tier.maxActiveWorkflows === 0 ? "∞" : formatNumber(tier.maxActiveWorkflows)} label="active workflows" />
                              <PlanFeature icon={<IconServerOutline18 className="h-5 w-5" />} value={tier.maxDatabaseTables === 0 ? "∞" : formatNumber(tier.maxDatabaseTables)} label="database tables" />
                              <PlanFeature icon={<IconMonitorOutline18 className="h-5 w-5" />} value={formatNumber(tier.cuAmount)} label="CU/month" />
                              <PlanFeature icon={<IconBrainOutline18 className="h-5 w-5" />} value={formatNumber(tier.aiCredits)} label="AI credits" />
                              <PlanFeature icon={<IconUserGroupOutline18 className="h-5 w-5" />} value={tier.maxUsers === 0 ? "∞" : formatNumber(tier.maxUsers)} label="users" />
                            </div>
                          </div>
                        </div>
                        {/* Selected radio indicator */}
                        {isSelected && <div className="bg-ds-brand-600 absolute right-[13.5px] top-[14px] flex size-4 items-center justify-center rounded-full">
                            <div className="size-[6px] rounded-full bg-white" />
                          </div>}
                      </button>;
            })}
                </div>

                {/* Enterprise card */}
                <div className="rounded-ds-2xl border-ds-border-default bg-ds-neutral-100 dark:bg-ds-neutral-800 border">
                  <div className="flex items-center gap-2 px-5 py-3">
                    <TierDots tier="enterprise" />
                    <span className="text-ds-text-subtle font-mono text-base font-medium uppercase tracking-[-0.32px]">
                      Enterprise
                    </span>
                  </div>
                  <div className="rounded-ds-xl border-ds-border-default bg-ds-surface-card mx-1 mb-1 flex items-start justify-between gap-6 border p-6">
                    <div>
                      <p className="text-ds-text-primary leading-label text-3xl font-semibold">Custom price</p>
                      <p className="text-ds-text-primary leading-label mt-2 text-sm dark:text-zinc-200">
                        For organizations with advanced automation needs. Unlimited everything, dedicated support,
                        custom SLA, CU & AI credit allocation tailored to your organization&apos;s needs.
                      </p>
                    </div>
                    <Button variant="link-brand" size="md" className="shrink-0 self-center whitespace-nowrap" onClick={() => {
                handleOpenChange(false);
                openSupport({
                  subject: "Enterprise plan inquiry",
                  text: "I'd like to learn more about the B3OS Enterprise plan for my organization."
                });
              }}>
                      Contact us →
                    </Button>
                  </div>
                </div>
              </div>}

            {error && <div className="px-6">
                <ErrorBanner message={error} />
              </div>}

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 pb-3 pt-3">
              <Button variant="white" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleContinue} disabled={!selectedTier || isSamePlan || !!cancelAt || pendingTierBlocks}>
                {currentTier !== "free" && !isDowngrade && !isSamePlan && selectedTier ? "Upgrade" : "Select plan"}
              </Button>
            </div>
          </>}

        {/* Step 2: Payment */}
        {step === "payment" && selectedTierInfo && <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => {
              setStep("select-tier");
              setError(null);
              // Drop the in-flight Stripe checkout session so re-entering
              // payment with a different tier mints a fresh one. Stripe's
              // EmbeddedCheckoutProvider binds clientSecret on mount and
              // doesn't react to later changes.
              setClientSecret(null);
              // Stop subscription polling — back-nav means the user is
              // browsing again, so don't yank them to the success screen
              // when the webhook lands.
              setIsWaitingForUpdate(false);
            }} className="rounded-ds-2xs hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800 p-1 transition-colors">
                  <IconArrowLeftOutline18 className="h-4 w-4" />
                </button>
                <div>
                  <DialogTitle className="heading-lg">
                    {currentTier === "free" ? "Subscribe" : "Upgrade"} to {getTierDisplayName(selectedTierInfo.name)}
                  </DialogTitle>
                  <DialogDescription className="body-sm mt-0.5">
                    {priceLabel}
                    {isYearly ? " (billed annually)" : ""}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {/* Payment method tabs */}
            <div className="rounded-ds-2xs border-ds-border-default bg-ds-neutral-100 dark:bg-ds-neutral-800 flex gap-1 border p-1">
              <button onClick={() => setPaymentMethod("stripe")} className={cn("rounded-ds-2xs flex flex-1 items-center justify-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-all", paymentMethod === "stripe" ? "text-ds-text-primary shadow-ds-pressed-inset-raised bg-ds-surface-card" : "text-ds-text-disabled hover:text-ds-text-subtle dark:text-ds-text-muted dark:hover:text-zinc-300")}>
                <IconCreditCardOutline18 className="h-3.5 w-3.5" />
                Credit Card
              </button>
              <button disabled className="rounded-ds-2xs dark:text-ds-text-subtle flex flex-1 cursor-not-allowed items-center justify-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-zinc-300">
                <IconCryptoWallet className="h-3.5 w-3.5" />
                Crypto
                <span className="micro-label rounded-ds-2xs bg-ds-neutral-200 dark:bg-ds-neutral-700 px-1.5 py-0.5 font-bold">
                  Soon
                </span>
              </button>
            </div>

            {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

            {isWaitingForUpdate && <div className="flex flex-col items-center gap-4 py-10 text-center">
                <UnicodeSpinner name="dna" className="text-ds-text-disabled text-2xl" />
                <div>
                  <h3 className="heading-lg">Updating your plan...</h3>
                  <p className="body-sm text-ds-text-muted mt-1.5">This usually takes a few seconds.</p>
                </div>
              </div>}

            {!isWaitingForUpdate && paymentMethod === "stripe" && <>
                {currentTier === "free" ?
          // New subscription — embedded Stripe Checkout
          <div className="space-y-4">
                    {clientSecret ? stripePromise ? <div className="min-h-[300px]">
                          <EmbeddedCheckoutProvider stripe={stripePromise} options={{
                clientSecret,
                onComplete: handleEmbeddedCheckoutComplete
              }}>
                            <EmbeddedCheckout />
                          </EmbeddedCheckoutProvider>
                        </div> : <ErrorBanner message="Payment is not available. Stripe is not configured." /> : <div className="flex flex-col items-center gap-4 py-8">
                        <p className="body-sm text-center">Enter your payment details to subscribe.</p>
                        <Button onClick={handleStripeNewSubscription} disabled={createCheckoutSession.isPending}>
                          {createCheckoutSession.isPending ? <span className="flex items-center gap-2">
                              <UnicodeSpinner name="breathe" className="text-sm" />
                              Loading...
                            </span> : "Continue to Payment"}
                        </Button>
                      </div>}
                  </div> :
          // Upgrade — backend handles Stripe update
          <div className="space-y-4 py-4">
                    <div className="rounded-ds-2xs border-ds-border-default dark:bg-ds-neutral-800/50 border bg-zinc-50 p-3">
                      <p className="text-ds-text-subtle dark:text-ds-text-disabled text-sm">
                        Your plan will be upgraded to{" "}
                        <strong className="text-ds-text-primary">{getTierDisplayName(selectedTierInfo.name)}</strong>{" "}
                        immediately.
                      </p>
                    </div>
                    <Button onClick={handleStripeUpgrade} disabled={upgradeSubscription.isPending || isWaitingForUpdate} className="w-full">
                      {upgradeSubscription.isPending ? <span className="flex items-center justify-center gap-2">
                          <UnicodeSpinner name="helix" className="text-sm" />
                          Upgrading...
                        </span> : `Upgrade to ${getTierDisplayName(selectedTierInfo.name)} — ${priceLabel}`}
                    </Button>
                  </div>}
              </>}

            {!isWaitingForUpdate && paymentMethod === "anyspend" && <div className="space-y-4 py-4">
                <p className="body-sm">Pay with USDC on Base using your connected wallet.</p>
                <Button onClick={handleAnySpendPayment} className="w-full">
                  Pay {chargeLabel} with Crypto
                </Button>
              </div>}
          </>}

        {/* Downgrade confirmation */}
        {step === "downgrade-confirm" && selectedTierInfo && <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => {
              setStep("select-tier");
              setError(null);
            }} className="rounded-ds-2xs hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800 p-1 transition-colors">
                  <IconArrowLeftOutline18 className="h-4 w-4" />
                </button>
                <DialogTitle className="heading-lg">
                  {isIntervalDowngrade ? `Switch to ${getTierDisplayName(selectedTierInfo.name)} Monthly` : `Downgrade to ${getTierDisplayName(selectedTierInfo.name)}`}
                </DialogTitle>
              </div>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <p className="text-ds-text-subtle dark:text-ds-text-disabled text-sm">
                {isIntervalDowngrade ? <>
                    Your billing will switch to{" "}
                    <strong className="text-ds-text-primary">
                      {getTierDisplayName(selectedTierInfo.name)} Monthly
                    </strong>{" "}
                    at the end of your current billing period.
                  </> : <>
                    Your plan will change to{" "}
                    <strong className="text-ds-text-primary">{getTierDisplayName(selectedTierInfo.name)}</strong> at the
                    end of your current billing period.
                  </>}
              </p>

              <div className="rounded-ds-2xs shadow-ds-status-inner-light border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-900/20">
                <p className="micro-label mb-2 font-semibold !text-amber-700 dark:!text-amber-400">
                  New Limits After Downgrade
                </p>
                <ul className="space-y-1 text-sm text-amber-800 dark:text-amber-300">
                  <li className="flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-amber-500" />
                    {formatNumber(selectedTierInfo.cuAmount)} CU/month
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-amber-500" />
                    {formatNumber(selectedTierInfo.aiCredits)} AI Credits
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-amber-500" />
                    {selectedTierInfo.maxActiveWorkflows === 0 ? "Unlimited" : selectedTierInfo.maxActiveWorkflows}{" "}
                    active workflows
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-amber-500" />
                    {selectedTierInfo.maxUsers === 0 ? "Unlimited" : selectedTierInfo.maxUsers} members
                  </li>
                </ul>
              </div>

              {/* Warnings when current usage exceeds new tier limits */}
              {subscriptionQuery.data && (() => {
            const usage = subscriptionQuery.data.usage;
            const warnings: string[] = [];
            if (selectedTierInfo.maxActiveWorkflows > 0 && usage.activeWorkflows > selectedTierInfo.maxActiveWorkflows) {
              warnings.push(`You have ${usage.activeWorkflows} active workflows but the new plan allows ${selectedTierInfo.maxActiveWorkflows}. You won't be able to publish new workflows until you're under the limit.`);
            }
            if (selectedTierInfo.maxUsers > 0 && usage.memberCount > selectedTierInfo.maxUsers) {
              warnings.push(`You have ${usage.memberCount} members but the new plan allows ${selectedTierInfo.maxUsers}. You won't be able to invite new members until you're under the limit.`);
            }
            if (warnings.length === 0) return null;
            return <div className="rounded-ds-2xs shadow-ds-status-inner-light border border-red-300 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                      <p className="micro-label mb-2 font-semibold !text-red-700 dark:!text-red-400">
                        Usage Exceeds New Limits
                      </p>
                      <ul className="space-y-1.5 text-sm text-red-800 dark:text-red-300">
                        {warnings.map(w => <li key={w} className="flex items-start gap-2">
                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500" />
                            {w}
                          </li>)}
                      </ul>
                    </div>;
          })()}

              {error && <ErrorBanner message={error} />}

              <div className="flex gap-3">
                <Button variant="white" onClick={() => {
              setStep("select-tier");
              setError(null);
            }} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleDowngradeConfirm} disabled={downgradeSubscription.isPending} className="flex-1">
                  {downgradeSubscription.isPending ? <span className="flex items-center justify-center gap-2">
                      <UnicodeSpinner name="snake" className="text-sm" />
                      Processing...
                    </span> : "Confirm Downgrade"}
                </Button>
              </div>
            </div>
          </>}

        {/* Success step */}
        {step === "success" && <div className="flex flex-col items-center gap-5 py-10 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100 shadow-[inset_0_-2px_4px_rgba(0,0,0,0.06)] dark:bg-green-900/30">
              <IconCheckOutline18 className="h-7 w-7 text-green-500" />
            </div>
            <div>
              <h3 className="heading-lg">Plan Updated</h3>
              <p className="body-sm mt-1.5">
                {isDowngrade && effectiveAt ? isIntervalDowngrade ? `Your billing will switch to ${getTierDisplayName(selectedTierInfo?.name ?? selectedTier ?? "")} Monthly on ${new Date(effectiveAt).toLocaleDateString()}.` : `Your plan will change to ${getTierDisplayName(selectedTierInfo?.name ?? selectedTier ?? "")} on ${new Date(effectiveAt).toLocaleDateString()}.` : `Your plan has been updated to ${getTierDisplayName(selectedTierInfo?.name ?? selectedTier ?? "")}.`}
              </p>
            </div>
            <Button onClick={() => handleOpenChange(false)}>Done</Button>
          </div>}
      </DialogContent>
    </Dialog>;
}

/* ─── Sub-components ──────────────────────────────────────────────── */

function UnicodeSpinner(t0) {
  const $ = _c(11);
  const {
    name: t1,
    className
  } = t0;
  const name = t1 === undefined ? "braille" : t1;
  const s = spinners[name];
  const [frame, setFrame] = useState(0);
  let t2;
  if ($[0] !== s.frames.length || $[1] !== s.interval) {
    t2 = () => {
      setFrame(0);
      const id = setInterval(() => setFrame(f => (f + 1) % s.frames.length), s.interval);
      return () => clearInterval(id);
    };
    $[0] = s.frames.length;
    $[1] = s.interval;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== name || $[4] !== s) {
    t3 = [name, s];
    $[3] = name;
    $[4] = s;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  const glyph = s.frames[frame % s.frames.length];
  let t4;
  if ($[6] !== className) {
    t4 = cn("inline-block font-mono", className);
    $[6] = className;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== glyph || $[9] !== t4) {
    t5 = <span className={t4}>{glyph}</span>;
    $[8] = glyph;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
const DOT_POSITIONS = [[3.33, 2], [8, 2], [12.67, 2], [3.33, 6.67], [8, 6.67], [12.67, 6.67], [3.33, 11.33], [8, 11.33], [12.67, 11.33]] as const;
const TIER_DOT_INDICES: Record<string, number[]> = {
  starter: [4, 6, 2],
  pro: [0, 1, 2, 4, 5, 8],
  biz: [0, 1, 2, 3, 5, 6, 7, 8],
  enterprise: [0, 1, 2, 3, 4, 5, 6, 7, 8]
};
function TierDots(t0) {
  const $ = _c(4);
  const {
    tier
  } = t0;
  const indices = TIER_DOT_INDICES[tier];
  if (!indices) {
    return null;
  }
  let t1;
  if ($[0] !== indices) {
    t1 = indices.map(_temp4);
    $[0] = indices;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0">{t1}</svg>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp4(i) {
  return <circle key={i} cx={DOT_POSITIONS[i][0]} cy={DOT_POSITIONS[i][1]} r="1.33" fill="#0c11ff" />;
}
function PlanFeature(t0) {
  const $ = _c(10);
  const {
    icon,
    value,
    label
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <span className="text-ds-text-disabled shrink-0">{icon}</span>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <span className="text-ds-text-primary leading-label text-sm font-medium dark:text-zinc-100">{value}</span>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== label) {
    t3 = <span className="text-ds-text-muted leading-label text-sm">{label}</span>;
    $[4] = label;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className="flex items-center gap-2 whitespace-nowrap">{t1}{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function PendingChangeBanner(t0) {
  const $ = _c(15);
  const {
    children,
    onKeep,
    isLoading,
    keepLabel
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <span>{children}</span>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isLoading) {
    t2 = isLoading && <span aria-hidden="true"><UnicodeSpinner name="breathe" className="text-sm" /></span>;
    $[2] = isLoading;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = isLoading ? "sr-only" : undefined;
  let t4;
  if ($[4] !== keepLabel || $[5] !== t3) {
    t4 = <span className={t3}>{keepLabel}</span>;
    $[4] = keepLabel;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== isLoading || $[8] !== onKeep || $[9] !== t2 || $[10] !== t4) {
    t5 = <Button variant="white" size="sm" onClick={onKeep} disabled={isLoading} className="ml-3 shrink-0">{t2}{t4}</Button>;
    $[7] = isLoading;
    $[8] = onKeep;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t1 || $[13] !== t5) {
    t6 = <div className="rounded-ds-2xs shadow-ds-status-inner-light mx-6 mt-4 flex items-center justify-between border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-400">{t1}{t5}</div>;
    $[12] = t1;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function ErrorBanner(t0) {
  const $ = _c(5);
  const {
    message,
    onDismiss
  } = t0;
  let t1;
  if ($[0] !== onDismiss) {
    t1 = onDismiss && <button onClick={onDismiss} className="ml-2 underline hover:no-underline">Dismiss</button>;
    $[0] = onDismiss;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== message || $[3] !== t1) {
    t2 = <div className="rounded-ds-2xs shadow-ds-status-inner-light border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">{message}{t1}</div>;
    $[2] = message;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}